import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { User } from "../../../types";
import { AvatarCropModal } from "./AvatarCropModal";

const MAX_BIO_LENGTH = 255;
const MAX_USERNAME_LENGTH = 24;
const ABOUT_SECTION_ANIMATION_MS = 200;
const SUPPORTED_AVATAR_EXTENSIONS = [".png", ".gif", ".jpg", ".jpeg", ".tiff", ".tif"] as const;
const SUPPORTED_AVATAR_EXTENSIONS_SET = new Set(
  SUPPORTED_AVATAR_EXTENSIONS.map((extension) => extension.slice(1)),
);

function isSupportedAvatarFile(file: File): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return !!extension && SUPPORTED_AVATAR_EXTENSIONS_SET.has(extension);
}

interface AboutYourselfSectionProps {
  user: User;
  username: string;
  fullName: string;
  description: string;
  fullNameError: string | null;
  pendingAvatarUrl: string | null;
  isPending: boolean;
  isUploadingAvatar: boolean;
  isProfileDirty: boolean;
  onUsernameChange: (value: string) => void;
  onFullNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onAvatarFileSelected: (file: File) => void;
  onSaveProfile: () => void;
  onAvatarError: (title: string, description: string) => void;
}

export function AboutYourselfSection({
  user,
  username,
  fullName,
  description,
  fullNameError,
  pendingAvatarUrl,
  isPending,
  isUploadingAvatar,
  isProfileDirty,
  onUsernameChange,
  onFullNameChange,
  onDescriptionChange,
  onAvatarFileSelected,
  onSaveProfile,
  onAvatarError,
}: AboutYourselfSectionProps) {
  const [t] = useTranslation();
  const aboutSection = useDisclosure({ defaultIsOpen: false });
  const [isAboutSectionRendered, setIsAboutSectionRendered] = useState(false);
  const [isAboutSectionVisible, setIsAboutSectionVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null);

  useEffect(() => {
    if (aboutSection.isOpen) {
      setIsAboutSectionRendered(true);
      const frameId = requestAnimationFrame(() => {
        setIsAboutSectionVisible(true);
      });
      return () => cancelAnimationFrame(frameId);
    }

    setIsAboutSectionVisible(false);

    if (!isAboutSectionRendered) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsAboutSectionRendered(false);
    }, ABOUT_SECTION_ANIMATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [aboutSection.isOpen, isAboutSectionRendered]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (!isSupportedAvatarFile(file)) {
      onAvatarError("Unsupported avatar file", "Use PNG, JPG, GIF, or TIFF.");
      e.target.value = "";
      return;
    }
    setAvatarCropFile(file);
    e.target.value = "";
  };

  const handleAvatarCropConfirm = (file: File) => {
    onAvatarFileSelected(file);
    setAvatarCropFile(null);
  };

  return (
    <VStack spacing={5} align="stretch">
      <AvatarCropModal
        file={avatarCropFile}
        isOpen={!!avatarCropFile}
        onClose={() => setAvatarCropFile(null)}
        onConfirm={handleAvatarCropConfirm}
      />

      <Box
        as="button"
        type="button"
        width="full"
        textAlign="left"
        onClick={aboutSection.onToggle}
        aria-expanded={aboutSection.isOpen}
      >
        <Flex align="center" justify="space-between">
          <Text fontSize="xl" fontFamily="heading" fontWeight={600} color="dune.dune">
            {t("screens.settings.aboutYourself")}
          </Text>
          {aboutSection.isOpen ? (
            <ChevronUpIcon boxSize={5} color="transparent.white.50" />
          ) : (
            <ChevronDownIcon boxSize={5} color="transparent.white.50" />
          )}
        </Flex>
      </Box>

      {isAboutSectionRendered && (
        <Box
          data-testid="about-yourself-panel"
          display="grid"
          gridTemplateRows={isAboutSectionVisible ? "1fr" : "0fr"}
          opacity={isAboutSectionVisible ? 1 : 0}
          transform={isAboutSectionVisible ? "translateY(0)" : "translateY(-6px)"}
          transition={`grid-template-rows ${ABOUT_SECTION_ANIMATION_MS}ms ease, opacity ${ABOUT_SECTION_ANIMATION_MS}ms ease, transform ${ABOUT_SECTION_ANIMATION_MS}ms ease`}
          pointerEvents={isAboutSectionVisible ? "auto" : "none"}
        >
          <VStack spacing={5} align="stretch" pt={2} overflow="hidden">
            <Flex align="center" gap={5}>
              <Avatar size="xl" name={user.name} src={pendingAvatarUrl || user.avatar_url} />
              <Box>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t("screens.settings.changeAvatar")}
                </Button>
                <Text fontSize="xs" color="transparent.white.30" mt={2}>
                  {t("screens.settings.avatarHint")}
                </Text>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept={SUPPORTED_AVATAR_EXTENSIONS.join(",")}
                style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0 }}
                onChange={handleAvatarChange}
              />
            </Flex>

            <FormControl>
              <FormLabel fontSize="sm" color="transparent.white.60">
                {t("screens.settings.username")}
              </FormLabel>
              <InputGroup>
                <InputLeftAddon
                  bg="transparent"
                  border="1px solid"
                  borderColor="transparent.white.20"
                  borderRight="none"
                  borderRightRadius={0}
                  color="transparent.white.30"
                  fontSize="sm"
                >
                  {t("screens.settings.usernamePrefix")}
                </InputLeftAddon>
                <Input
                  fontSize="16px"
                  value={username}
                  onChange={(e) => onUsernameChange(e.target.value)}
                  maxLength={MAX_USERNAME_LENGTH}
                  borderLeftRadius={0}
                />
              </InputGroup>
              <Text fontSize="xs" color="transparent.white.30" mt={1}>
                {t("screens.settings.usernameHint", { max: MAX_USERNAME_LENGTH })}
              </Text>
            </FormControl>

            <FormControl isRequired isInvalid={!!fullNameError}>
              <FormLabel fontSize="sm" color="transparent.white.60">
                {t("screens.settings.fullName")}
              </FormLabel>
              <Input
                fontSize="16px"
                value={fullName}
                onChange={(e) => onFullNameChange(e.target.value)}
                maxLength={150}
                placeholder={t("screens.settings.fullNamePlaceholder")}
              />
              {fullNameError ? (
                <FormErrorMessage>{fullNameError}</FormErrorMessage>
              ) : (
                <Text fontSize="xs" color="transparent.white.30" mt={1}>
                  {t("screens.settings.fullNameHint")}
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color="transparent.white.60">
                {t("screens.settings.shortBio")}
              </FormLabel>
              <Textarea
                fontSize="16px"
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                maxLength={MAX_BIO_LENGTH}
                placeholder={t("screens.settings.bioPlaceholder")}
                minH="100px"
                resize="vertical"
              />
              <Text fontSize="xs" color="transparent.white.30" mt={1} textAlign="right">
                {description.length}/{MAX_BIO_LENGTH}
              </Text>
            </FormControl>

            <Button
              variant="primary"
              size="sm"
              onClick={onSaveProfile}
              isLoading={isPending || isUploadingAvatar}
              isDisabled={!isProfileDirty || isPending || isUploadingAvatar}
            >
              {t("screens.settings.saveProfile")}
            </Button>
          </VStack>
        </Box>
      )}
    </VStack>
  );
}
